'use client'
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import react from "react";
import { Drawer } from "vaul";
import { cleanProps, cn } from "../../utils/index.js";
import { CdnIcon } from "../CdnIcon/index.js";
import { Heading } from "../Heading/index.js";
import { IconButton } from "../IconButton/index.js";
import { ScrollArea } from "../ScrollArea/index.js";
import { Span } from "../Text/index.js";
import { drawerVariants } from "./Drawer.variants.js";
const visuallyHiddenClass = 'sr-only';
function Drawer_Drawer({ children, description, footer, headingCustomNode, headingIconName, headingRightNode, headingText, open, defaultOpen, showCloseButton = true, showDragHandle = true, title, triggerNode, classNames, onNaturalClose, onOpenChange, ...props }) {
    const { overlaySlot, headingSlot, headingSlotLeft, headingRightNodeSlot, closeButtonSlot, headingTextSlot, headingIconSlot, bodySlot, scrollAreaSlot, contentSlot, handleSlot, footerSlot } = drawerVariants({
        showDragHandle,
        customBody: children ? 'string' != typeof children : false,
        customFooter: !!footer,
        customHeader: !!headingCustomNode,
        showCloseButton,
        useSnapping: !!props.snapPoints,
        size: 'bottom' !== props.direction && void 0 !== props.direction ? 'sm' : props.size,
        ...props
    });
    const contentSlotClass = cn(contentSlot(), classNames?.contentSlot);
    const [snap, setSnap] = react.useState(props.snapPoints?.[0] || null);
    const handleOpenChange = (open)=>{
        if (onNaturalClose && !open) onNaturalClose();
        onOpenChange?.(open);
    };
    const handleClose = ()=>{
        if (onNaturalClose) onNaturalClose();
        onOpenChange?.(false);
    };
    return /*#__PURE__*/ jsxs(Drawer.Root, {
        ...props,
        open: open,
        onOpenChange: handleOpenChange,
        defaultOpen: void 0 === open ? defaultOpen : false,
        ...cleanProps({
            activeSnapPoint: snap,
            setActiveSnapPoint: setSnap
        }, [
            'activeSnapPoint',
            'setActiveSnapPoint'
        ]),
        children: [
            /*#__PURE__*/ jsx(Drawer.Trigger, {
                asChild: true,
                children: triggerNode
            }),
            /*#__PURE__*/ jsxs(Drawer.Portal, {
                children: [
                    /*#__PURE__*/ jsx(Drawer.Overlay, {
                        className: overlaySlot()
                    }),
                    /*#__PURE__*/ jsxs(Drawer.Content, {
                        className: contentSlotClass,
                        children: [
                            showDragHandle && /*#__PURE__*/ jsx("div", {
                                className: "absolute left-1/2 top-0",
                                children: /*#__PURE__*/ jsx(Drawer.Handle, {
                                    className: handleSlot()
                                })
                            }),
                            /*#__PURE__*/ jsxs("div", {
                                className: headingSlot(),
                                children: [
                                    headingCustomNode ? /*#__PURE__*/ jsx(Drawer.Title, {
                                        className: "w-full",
                                        children: headingCustomNode
                                    }) : headingText ? /*#__PURE__*/ jsxs(Fragment, {
                                        children: [
                                            /*#__PURE__*/ jsxs("div", {
                                                className: headingSlotLeft(),
                                                children: [
                                                    headingIconName && /*#__PURE__*/ jsx(CdnIcon, {
                                                        iconName: headingIconName,
                                                        size: "24",
                                                        className: headingIconSlot()
                                                    }),
                                                    /*#__PURE__*/ jsx(Drawer.Title, {
                                                        asChild: true,
                                                        children: /*#__PURE__*/ jsx(Heading, {
                                                            as: "h4",
                                                            className: headingTextSlot(),
                                                            children: headingText
                                                        })
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ jsx("div", {
                                                className: headingRightNodeSlot(),
                                                children: headingRightNode
                                            })
                                        ]
                                    }) : /*#__PURE__*/ jsxs(Fragment, {
                                        children: [
                                            /*#__PURE__*/ jsx("div", {
                                                className: "flex-1"
                                            }),
                                            /*#__PURE__*/ jsx(Drawer.Title, {
                                                asChild: true,
                                                children: /*#__PURE__*/ jsx("span", {
                                                    className: visuallyHiddenClass,
                                                    children: title
                                                })
                                            })
                                        ]
                                    }),
                                    showCloseButton && /*#__PURE__*/ jsx("div", {
                                        className: closeButtonSlot(),
                                        children: /*#__PURE__*/ jsx(Drawer.Close, {
                                            asChild: true,
                                            children: /*#__PURE__*/ jsx(IconButton, {
                                                iconName: "close",
                                                type: "primary",
                                                shadow: true,
                                                size: "md",
                                                "aria-label": "Close Drawer",
                                                emphasis: "bold",
                                                onClick: handleClose
                                            })
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ jsxs(ScrollArea, {
                                className: scrollAreaSlot(),
                                drawerBody: true,
                                children: [
                                    /*#__PURE__*/ jsxs("div", {
                                        className: bodySlot(),
                                        children: [
                                            description && /*#__PURE__*/ jsx(Drawer.Description, {
                                                className: visuallyHiddenClass,
                                                children: description
                                            }),
                                            !description && !children && /*#__PURE__*/ jsx(Drawer.Description, {
                                                className: visuallyHiddenClass,
                                                children: /*#__PURE__*/ jsx("span", {
                                                    className: visuallyHiddenClass,
                                                    children: "No content available"
                                                })
                                            }),
                                            'string' == typeof children ? /*#__PURE__*/ jsx(Span, {
                                                textStyle: "body2",
                                                className: "px-4",
                                                children: children
                                            }) : children
                                        ]
                                    }),
                                    footer && /*#__PURE__*/ jsx("div", {
                                        className: footerSlot(),
                                        children: footer
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
export { Drawer_Drawer as Drawer };
